import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Card,
    Typography,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
    Divider,
    CircularProgress,
    Paper as MuiPaper,
    CardContent,
    Pagination,
    Select,
    MenuItem,
    TextField,
    InputAdornment
} from "@mui/material";
import { FaUsers, FaRegClipboard, FaChartPie, FaSearch } from "react-icons/fa";
import ReferalByPatient from "../chart/ReferalByPatient";
import ProcedureTable from "../tables/ProcedureTable";
import { get } from "../../utility/fetch";
import TemplateSelector from "../layouts/TemplateSelector";
import LabReportTemplates from "./LabReportPage";

const filterWalkinOptions = [
    // { value: "all", label: "All" },
    { value: "all", label: "All" },
    { value: "Name", label: "Name" },
    { value: "Email", label: "Email" },
    { value: "PhoneNumber", label: "Phone Number" },
    { value: "PatientRef", label: "Patient Reference" },
]

const filterReferralOptions = [
    { value: "all", label: "All" },
    { value: "Name", label: "Name" },
    { value: "Email", label: "Email" },
    { value: "PhoneNumber", label: "Phone Number" },
    { value: "PatientRef", label: "Patient Reference" },
]

const StatCard = ({ label, number, icon, loading }) => (
    <Card
        sx={{
            minHeight: 120,
            display: "flex",
            alignItems: "center",
            position: "relative",
            borderRadius: 2,
            boxShadow: "0 6px 18px rgba(40,167,69,0.08)",
            overflow: "hidden",
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", p: 2, gap: 2, width: "100%" }}>
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 1,
                    background: "linear-gradient(135deg, rgba(40,167,69,0.12), rgba(40,167,69,0.06))",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#28a745",
                    flexShrink: 0,
                }}
            >
                {icon}
            </Box>

            <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#234d20" }}>
                    {loading ? <Skeleton width={80} /> : number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {label}
                </Typography>
            </Box>
        </Box>
    </Card>
);

const ChartCard = ({ title, children, loading }) => (
    <Card sx={{ p: 2, borderRadius: 2, height: "100%", boxShadow: "0 6px 18px rgba(0,0,0,0.04)" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#234d20" }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {loading ? "Loading..." : "Last 6 months"}
            </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {loading ? <CircularProgress size={36} sx={{ color: "#28a745" }} /> : children}
        </Box>
    </Card>
);

export default function Reports() {
    // summary / charts
    const [summary, setSummary] = useState(null);
    const [walkInPercent, setWalkInPercent] = useState({});
    const [walkinReferral, setWakinReferral] = useState([]);
    const [referrals, setReferrals] = useState([]);
    const [graphData, setGraphData] = useState([]);

    // toggles & table data
    const [patientType, setPatientType] = useState('walkin');
    const [referredPatients, setReferredPatients] = useState([]);
    const [walkInPatients, setWalkInPatients] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);

    // loading states
    const [isLoadingSummary, setIsLoadingSummary] = useState(true);
    const [isLoadingGraph, setIsLoadingGraph] = useState(true);
    const [isLoadingReferrals, setIsLoadingReferrals] = useState(true);
    const [isLoadingWalkinReferral, setIsLoadingWalkinReferral] = useState(true);

    // Add pagination states
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(10); // You can make this configurable if needed
    const [filterOn, setFilterOn] = useState("all"); // Add more filter options if needed
    const [filterQuery, setFilterQuery] = useState(""); // Add filter functionality if needed
    const [color, setColor] = useState("");
    const [providerTemplateId, setProviderTemplateId] = useState();
    const [template, setTemplate] = useState();


    const [currentTemplate, setCurrentTemplate] = useState();

    const userId = sessionStorage.getItem("healthCareProviderId");

    const defaultSummary = {
        numberOfPatients: 0,
        numberOfWalkInPatients: 0,
        numberOfReferredPatients: 0,
        numberOfPendingLabWork: 0,
    };

    const fetchSummary = async () => {
        setIsLoadingSummary(true);
        try {
            const res = await get(`/dashboard/lab-summary?labId=${userId}`);
            setSummary(res && res.length > 0 ? res[0] : defaultSummary);
        } catch (e) {
            console.error("FAILED TO FETCH SUMMARY", e);
            setSummary(defaultSummary);
        } finally {
            setIsLoadingSummary(false);
        }
    };

    const fetchWalkinReferral = async () => {
        setIsLoadingWalkinReferral(true);
        try {
            const res = await get(`/dashboard/monthly-walkin-referral-count?labId=${userId}`);
            if (!res || res.length === 0) {
                setWakinReferral([]);
                setWalkInPercent({});
            } else {
                const monthlyData = res.map((item) => {
                    const [year, month] = item.month.split("-");
                    return {
                        month: new Date(0, parseInt(month) - 1).toLocaleString("default", { month: "short" }),
                        walkIn: item.walkInCount || 0,
                        referral: item.referralCount || 0,
                    };
                });
                setWakinReferral(monthlyData);

                const totalWalkIn = res.reduce((s, i) => s + (i.walkInCount || 0), 0);
                const totalReferral = res.reduce((s, i) => s + (i.referralCount || 0), 0);
                const total = totalWalkIn + totalReferral;
                const percentageSplit =
                    total > 0
                        ? { WalkIn: Math.round((totalWalkIn / total) * 100), Referral: Math.round((totalReferral / total) * 100) }
                        : { WalkIn: 0, Referral: 0 };
                setWalkInPercent(percentageSplit);
            }
        } catch (e) {
            console.error("FAILED TO FETCH WALK-IN/REFERRALS", e);
            setWakinReferral([]);
            setWalkInPercent({});
        } finally {
            setIsLoadingWalkinReferral(false);
        }
    };

    const fetchReferrals = async () => {
        setIsLoadingReferrals(true);
        try {
            const res = await get(`/dashboard/monthly-referrals?labId=${userId}`);
            if (!res || res.length === 0) {
                setReferrals([]);
            } else {
                const grouped = res.reduce((acc, curr) => {
                    const [year, month] = curr.month.split("-");
                    const monthName = new Date(0, parseInt(month) - 1).toLocaleString("default", { month: "short" });
                    acc[monthName] = (acc[monthName] || 0) + (curr.numberOfReferrals || 0);
                    return acc;
                }, {});
                const chartData = Object.entries(grouped).map(([month, value]) => ({ month, referrals: value }));
                setReferrals(chartData);
            }
        } catch (e) {
            console.error("FAILED TO FETCH REFERRALS", e);
            setReferrals([]);
        } finally {
            setIsLoadingReferrals(false);
        }
    };

    const fetchPatientBreakdown = async () => {
        setIsLoadingGraph(true);
        try {
            const res = await get(`/dashboard/monthly-patient-type-breakdown?labId=${userId}`);
            const transformed = (res || []).map((item) => {
                const [year, month] = item.month.split("-");
                return { month: parseInt(month), outPatient: item.outpatients || 0, inPatient: item.inpatients || 0 };
            });
            const allMonths = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, outPatient: 0, inPatient: 0 }));
            const merged = allMonths.map((m) => {
                const match = transformed.find((t) => t.month === m.month);
                return { ...m, outPatient: match ? match.outPatient : 0, inPatient: match ? match.inPatient : 0, name: new Date(0, m.month - 1).toLocaleString("default", { month: "short" }) };
            });
            setGraphData(merged);
        } catch (e) {
            console.error("FAILED TO FETCH Breakdown", e);
            setGraphData([]);
        } finally {
            setIsLoadingGraph(false);
        }
    };

    // table fetchers
    // helper to normalize API responses
    const normalizeListResponse = (res) => {
        // common shapes: res.data.recordList + res.data.metadata
        const recordList = res?.data?.recordList || res?.resultList || res?.data || res?.recordList || [];
        const metadata = res?.data?.metadata || res?.paginationMetadata || res?.metadata || {};
        const totalItems = metadata?.totalItems ?? metadata?.total ?? 0;
        const totalPagesFromMeta = metadata?.totalPages || metadata?.pageCount || Math.ceil(totalItems / pageSize) || 1;
        return { list: recordList, totalPages: totalPagesFromMeta, totalItems };
    };

    const fetchReferredPatients = async (pageNum = page, filter = filterOn, query = filterQuery) => {
        setTableLoading(true);
        try {
            let res;
            const hasQuery = (query || "").toString().trim() !== "";
            const useFilter = hasQuery || (filter && filter !== "all");

            if (useFilter) {
                // existing filter endpoint (keeps current behavior for searches)
                res = await get(`/patientlabreport/filter-list/${filter}/${query || ' '}/${pageNum}/${pageSize}`);
            } else {
                // new: use paginated list endpoint when no search/filter
                res = await get(`/patientlabreport/internal-lab-report/list/${pageNum}/${pageSize}`);
            }

            const { list, totalPages } = normalizeListResponse(res);
            setReferredPatients(list || []);
            setTotalPages(totalPages || 1);
        } catch (e) {
            console.error("FAILED TO FETCH REFERRED PATIENTS", e);
            setReferredPatients([]);
            setTotalPages(1);
        } finally {
            setTableLoading(false);
        }
    };

    const fetchWalkinPatients = async (pageNum = page, filter = filterOn, query = filterQuery) => {
        setTableLoading(true);
        try {
            let res;
            const hasQuery = (query || "").toString().trim() !== "";
            const useFilter = hasQuery || (filter && filter !== "all");

            if (useFilter) {
                // keep existing search endpoint when user searches or applies a specific filter
                res = await get(`/walkinpatientlabreport/filter-list/true/${filter}?filterQuery=${query || ' '}&pageNumber=${pageNum}&pageSize=${pageSize}`);
            } else {
                // new: use paginated list endpoint when no search/filter
                res = await get(`/walkinpatientlabreport/list/true/All?pageNumber=${pageNum}&pageSize=${pageSize}`);
            }

            const { list, totalPages } = normalizeListResponse(res);
            setWalkInPatients(list || []);
            setTotalPages(totalPages || 1);
        } catch (e) {
            console.error("FAILED TO FETCH WALK-IN PATIENTS", e);
            setWalkInPatients([]);
            setTotalPages(1);
        } finally {
            setTableLoading(false);
        }
    };


    // Modify fetchTableData to include filter params
    const fetchTableData = async (type = patientType, pageNum = page, filter = filterOn, query = filterQuery) => {
        if (type === 'referred') {
            await fetchReferredPatients(pageNum, filter, query);
        } else {
            await fetchWalkinPatients(pageNum, filter, query);
        }
    };

    // Add page change handler
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        fetchTableData(patientType, newPage);
    };

    // Add debounce for search
    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    };

    // Handle filter changes
    const handleFilterOnChange = (event) => {
        setFilterOn(event.target.value);
        setPage(1); // Reset to first page
        fetchTableData(patientType, 1, event.target.value, filterQuery);
    };

    const handleFilterQueryChange = debounce((event) => {
        const value = event.target.value;
        setFilterQuery(value);
        setPage(1); // Reset to first page
        fetchTableData(patientType, 1, filterOn, value);
    }, 500); // 500ms debounce


    const getCurrentTemplate = async () => {
        // ... (no changes)
        try {
            const res = await get(`/labreportTemplate/lab-request/${sessionStorage.getItem('healthCareProviderId')}`);
            const data = res?.data || res || {};
            const tid = data?.formId || data?.id || data?.template?.id;
            const color = data?.colorId;
            if (tid) {
                setProviderTemplateId(tid);
                setTemplate(data)
                setColor(color);
                if (tid) setCurrentTemplate(tid);
            }
        } catch (e) {
            // ignore
        }
    };


    useEffect(() => {
        fetchSummary();
        fetchWalkinReferral();
        fetchReferrals();
        fetchPatientBreakdown();
        setPage(1); // Reset to first page when switching types
        fetchTableData(patientType, 1);
        getCurrentTemplate();
        // eslint-disable-next-line
    }, [patientType]);

    // Add this before the return statement to handle cleanup
    useEffect(() => {
        return () => {
            // Clean up any pending debounced calls
            handleFilterQueryChange.cancel && handleFilterQueryChange.cancel();
        };
    }, []);

    const handleToggleChange = (event, newValue) => {
        if (newValue !== null) {
            setPatientType(newValue);
            fetchTableData(newValue);
        }
    };

    const tableData = patientType === "referred" ? referredPatients : walkInPatients;
    const filterOptions = patientType === "referred" ? filterReferralOptions : filterWalkinOptions;

    return (
        <Box className="m-t-40" sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", width: "100%" }}>
            <Box sx={{ maxWidth: "1200px", mx: "auto" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#164e2e" }}>
                            Reports
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Overview of lab activity — referrals, walk-ins and trends
                        </Typography>
                    </Box>

                    <MuiPaper elevation={1} sx={{ display: "flex", alignItems: "center", p: 0.5, borderRadius: 2 }}>
                        <ToggleButtonGroup value={patientType} exclusive onChange={handleToggleChange} size="small" sx={{ display: "flex", p: 0.5 }}>
                            <ToggleButton
                                value="referred"
                                sx={{
                                    px: 2,
                                    color: patientType === "referred" ? "#fff" : "#28a745",
                                    background: patientType === "referred" ? "#28a745" : "#fff",
                                    borderRadius: 1,
                                    border: 'none',
                                    "&:hover": { background: patientType === "referred" ? "#1f7a2f" : "#f0fff4" }
                                }}
                            >
                                Referred
                            </ToggleButton>
                            <ToggleButton
                                value="walkin"
                                sx={{
                                    px: 2,
                                    color: patientType === "walkin" ? "#fff" : "#28a745",
                                    background: patientType === "walkin" ? "#28a745" : "#fff",
                                    borderRadius: 1,
                                    border: 'none',
                                    "&:hover": { background: patientType === "walkin" ? "#1f7a2f" : "#f0fff4" }
                                }}
                            >
                                Walk-in
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </MuiPaper>
                </Stack>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <StatCard label="Total Patients" number={summary?.numberOfPatients ?? 0} icon={<FaUsers size={20} />} loading={isLoadingSummary} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard label="Walk-in Patients" number={summary?.numberOfWalkInPatients ?? 0} icon={<FaRegClipboard size={18} />} loading={isLoadingSummary} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <StatCard label="Pending Lab Work" number={summary?.numberOfPendingLabWork ?? 0} icon={<FaChartPie size={18} />} loading={isLoadingSummary} />
                    </Grid>
                </Grid>

                <Grid xs={12} md={12} container spacing={2}>
                    <Grid item xs={12} md={7}>
                        <ChartCard title="Walk-in vs Referrals" loading={isLoadingWalkinReferral}>
                            {!isLoadingWalkinReferral && <ReferalByPatient propData={walkInPercent} title="Walk-in vs Referrals" />}
                        </ChartCard>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <ChartCard title="Monthly Referrals" loading={isLoadingReferrals}>
                            {!isLoadingReferrals ? (
                                <Box sx={{ width: "100%", px: 2 }}>
                                    {referrals.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">No referral data available</Typography>
                                    ) : (
                                        <Stack spacing={1} sx={{ width: "100%" }}>
                                            {referrals.map((r) => (
                                                <MuiPaper key={r.month} variant="outlined" sx={{ p: 1, display: "flex", justifyContent: "space-between", alignItems: "center", borderRadius: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{r.month}</Typography>
                                                    <Typography variant="body2" color="text.secondary">{r.referrals}</Typography>
                                                </MuiPaper>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            ) : null}
                        </ChartCard>
                    </Grid>
                </Grid>

                <Box sx={{ mt: 3 }}>
                    <Card sx={{ p: 2, borderRadius: 2 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, color: "#164e2e" }}>
                            Detailed Data
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        {/* Add Filter Controls */}
                        <Box sx={{
                            mb: 2,
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            flexWrap: { xs: 'wrap', md: 'nowrap' }
                        }}>
                            <Select
                                size="small"
                                value={filterOn}
                                onChange={handleFilterOnChange}
                                sx={{ minWidth: 120 }}
                            >
                                {filterOptions.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>

                            <TextField
                                size="small"
                                placeholder="Search..."
                                onChange={handleFilterQueryChange}
                                sx={{ flex: 1 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FaSearch />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        <Box sx={{ minHeight: 160 }}>
                            {(isLoadingSummary || isLoadingGraph) ? (
                                <Stack spacing={1} sx={{ width: "100%" }}>
                                    {[...Array(4)].map((_, i) => (
                                        <Skeleton key={i} variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
                                    ))}
                                </Stack>
                            ) : (
                                <>
                                    <ProcedureTable
                                        loading={tableLoading}
                                        readOnly={true}
                                        data={patientType === "referred" ? referredPatients : walkInPatients}
                                        isWalkin={patientType === 'walkin'}
                                        currentPage={page}
                                        currentTemplate = {providerTemplateId}
                                        template={template}
                                        color = {color}
                                    />

                                
                                    {/* Add Pagination */}

                                    <Box sx={{
                                        mt: 2,
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        gap: 2
                                    }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Page {page} of {totalPages}
                                        </Typography>
                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={handlePageChange}
                                            color="primary"
                                            shape="rounded"
                                            size="medium"
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
}